// PIQ Advocate Engine - Opus 4.5 Build
// Advocates List Page - View, filter, and export advocate data

import React, { useState } from 'react'
import { useLocation } from 'wouter'
import { 
  Users,
  Download,
  Search,
  Filter,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Star,
  TrendingUp,
  DollarSign,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle,
  Mail,
  Copy,
  FileSpreadsheet,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../lib/api-client'

// ============================================================================
// TYPES
// ============================================================================

interface Advocate {
  advocate_id: string
  primary_handle: string
  primary_platform: string
  display_name: string | null
  profile_url: string | null
  avatar_url: string | null
  bio: string | null
  follower_count: number | null
  score: number | null
  tier: string | null
  status: string
  total_mentions: number
  positive_mentions: number
  total_revenue_generated: number
  total_commissions_paid: number
  first_detected_at: string
  last_mention_at: string | null
  activated_at: string | null
  tags: string[] | null
}

interface AdvocatesResponse {
  advocates: Advocate[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
  summary: {
    tierCounts: Record<string, number>
    total: number
  }
}

// ============================================================================
// PLATFORM CONFIG
// ============================================================================

const PLATFORM_COLORS: Record<string, string> = {
  tiktok: 'bg-black text-white',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
  twitter: 'bg-black text-white',
  reddit: 'bg-orange-500 text-white',
  youtube: 'bg-red-600 text-white',
  facebook: 'bg-blue-600 text-white',
}

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  invited: 'bg-yellow-100 text-yellow-700',
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  declined: 'bg-red-100 text-red-700',
}

const TIER_COLORS: Record<string, string> = {
  A: 'bg-emerald-500 text-white',
  B: 'bg-blue-500 text-white',
  C: 'bg-gray-500 text-white',
}

// ============================================================================
// HOOKS
// ============================================================================

function useAdvocates(filters: {
  page: number
  limit: number
  platform?: string
  status?: string
  search?: string
}) {
  return useQuery({
    queryKey: ['advocate-opus', 'advocates', filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(filters.page),
        limit: String(filters.limit),
      })
      if (filters.platform) params.set('platform', filters.platform)
      if (filters.status) params.set('status', filters.status)
      if (filters.search) params.set('search', filters.search)
      
      const response = await apiClient.get<{ success: boolean; data: AdvocatesResponse }>(
        `/advocate-opus/advocates?${params.toString()}`
      )
      return response.data
    },
  })
}

// ============================================================================
// COMPONENTS
// ============================================================================

function PlatformBadge({ platform }: { platform: string }) {
  const colorClass = PLATFORM_COLORS[platform] || 'bg-gray-500 text-white'
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {platform}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const colorClass = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${colorClass}`}>
      {status}
    </span>
  )
}

function TierBadge({ tier }: { tier: string | null }) {
  if (!tier) return <span className="text-gray-400 text-sm">—</span>
  const colorClass = TIER_COLORS[tier] || 'bg-gray-500 text-white'
  return (
    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${colorClass}`}>
      {tier}
    </span>
  )
}

function ExportButton({ advocates }: { advocates: Advocate[] }) {
  const [copied, setCopied] = useState(false)

  const exportToCSV = () => {
    const headers = [
      'Handle',
      'Platform',
      'Display Name',
      'Profile URL',
      'Followers',
      'Score',
      'Tier',
      'Status',
      'Total Mentions',
      'Positive Mentions',
      'Revenue Generated',
      'First Detected',
      'Last Mention',
    ]
    
    const rows = advocates.map(a => [
      `@${a.primary_handle}`,
      a.primary_platform,
      a.display_name || '',
      a.profile_url || '',
      a.follower_count?.toString() || '',
      a.score?.toFixed(1) || '',
      a.tier || '',
      a.status,
      (a.total_mentions || 0).toString(),
      (a.positive_mentions || 0).toString(),
      `$${(a.total_revenue_generated || 0).toFixed(2)}`,
      new Date(a.first_detected_at).toLocaleDateString(),
      a.last_mention_at ? new Date(a.last_mention_at).toLocaleDateString() : '',
    ])
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `advocates_export_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  const copyToClipboard = () => {
    const text = advocates.map(a => 
      `@${a.primary_handle} (${a.primary_platform}) - ${a.follower_count?.toLocaleString() || 'N/A'} followers - ${a.profile_url || 'No URL'}`
    ).join('\n')
    
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative group">
      <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
        <Download className="w-4 h-4" />
        Export
      </button>
      <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
        <button
          onClick={exportToCSV}
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
        >
          <FileSpreadsheet className="w-4 h-4 text-green-600" />
          Export to CSV
        </button>
        <button
          onClick={copyToClipboard}
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
        >
          <Copy className="w-4 h-4 text-blue-600" />
          {copied ? 'Copied!' : 'Copy to Clipboard'}
        </button>
      </div>
    </div>
  )
}

function AdvocateRow({ advocate, onClick }: { advocate: Advocate; onClick: () => void }) {
  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      {/* Avatar + Handle */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {advocate.avatar_url ? (
            <img 
              src={advocate.avatar_url} 
              alt={advocate.primary_handle}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500 font-medium">
                {advocate.primary_handle[0]?.toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <div className="font-medium text-gray-900">
              @{advocate.primary_handle}
            </div>
            <div className="text-sm text-gray-500">
              {advocate.display_name || '—'}
            </div>
          </div>
        </div>
      </td>

      {/* Platform */}
      <td className="px-4 py-3">
        <PlatformBadge platform={advocate.primary_platform} />
      </td>

      {/* Followers */}
      <td className="px-4 py-3 text-right">
        <span className="font-medium">
          {advocate.follower_count?.toLocaleString() || '—'}
        </span>
      </td>

      {/* Tier */}
      <td className="px-4 py-3 text-center">
        <TierBadge tier={advocate.tier} />
      </td>

      {/* Score */}
      <td className="px-4 py-3 text-right">
        <span className={`font-medium ${
          (advocate.score || 0) >= 75 ? 'text-green-600' :
          (advocate.score || 0) >= 50 ? 'text-blue-600' :
          'text-gray-600'
        }`}>
          {advocate.score?.toFixed(1) || '—'}
        </span>
      </td>

      {/* Mentions */}
      <td className="px-4 py-3 text-right">
        <span className="font-medium">{advocate.total_mentions || 0}</span>
        {(advocate.positive_mentions || 0) > 0 && (
          <span className="text-green-600 text-sm ml-1">
            (+{advocate.positive_mentions})
          </span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusBadge status={advocate.status} />
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {advocate.profile_url && (
            <a
              href={advocate.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1 hover:bg-gray-100 rounded"
              title="View Profile"
            >
              <ExternalLink className="w-4 h-4 text-gray-500" />
            </a>
          )}
        </div>
      </td>
    </tr>
  )
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AdvocatesList() {
  const [, navigate] = useLocation()
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [platformFilter, setPlatformFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading, error, refetch } = useAdvocates({
    page,
    limit: 25,
    platform: platformFilter || undefined,
    status: statusFilter || undefined,
    search: searchQuery || undefined,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    refetch()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-7 h-7 text-purple-600" />
                Advocates
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                  Opus Build
                </span>
              </h1>
              <p className="text-gray-500 mt-1">
                {data?.summary.total || 0} advocates identified • Ready for outreach
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/advocate-opus/discovery')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Search className="w-4 h-4" />
                Find More
              </button>
              {data && data.advocates.length > 0 && (
                <ExportButton advocates={data.advocates} />
              )}
            </div>
          </div>

          {/* Stats Cards */}
          {data && (
            <div className="grid grid-cols-4 gap-4 mt-6">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="text-emerald-600 text-sm font-medium">Tier A (Top)</div>
                <div className="text-2xl font-bold text-emerald-700">
                  {data.summary?.tierCounts?.['A'] || 0}
                </div>
                <div className="text-emerald-600 text-xs">Score 75-100</div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="text-blue-600 text-sm font-medium">Tier B</div>
                <div className="text-2xl font-bold text-blue-700">
                  {data.summary?.tierCounts?.['B'] || 0}
                </div>
                <div className="text-blue-600 text-xs">Score 50-74</div>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                <div className="text-gray-600 text-sm font-medium">Tier C</div>
                <div className="text-2xl font-bold text-gray-700">
                  {data.summary?.tierCounts?.['C'] || 0}
                </div>
                <div className="text-gray-600 text-xs">Score 0-49</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="text-purple-600 text-sm font-medium">Total</div>
                <div className="text-2xl font-bold text-purple-700">
                  {data.summary?.total || 0}
                </div>
                <div className="text-purple-600 text-xs">All Advocates</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-4 mt-6">
            <form onSubmit={handleSearch} className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by handle or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </form>

            <select
              value={platformFilter}
              onChange={(e) => {
                setPlatformFilter(e.target.value)
                setPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Platforms</option>
              <option value="tiktok">TikTok</option>
              <option value="instagram">Instagram</option>
              <option value="twitter">Twitter/X</option>
              <option value="youtube">YouTube</option>
              <option value="reddit">Reddit</option>
              <option value="facebook">Facebook</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="invited">Invited</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-gray-500">Failed to load advocates</p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        {data && (
          <>
            {data.advocates.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No advocates yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Mark social mentions as advocates to add them here
                </p>
                <button
                  onClick={() => navigate('/advocate-opus/discovery')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Find Advocates
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Advocate
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Platform
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Followers
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Tier
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Mentions
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.advocates.map(advocate => (
                      <AdvocateRow
                        key={advocate.advocate_id}
                        advocate={advocate}
                        onClick={() => navigate(`/advocate-opus/advocates/${advocate.advocate_id}`)}
                      />
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {data.pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      Showing {((data.pagination.page - 1) * data.pagination.limit) + 1} to{' '}
                      {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{' '}
                      {data.pagination.total} advocates
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={!data.pagination.hasPrev}
                        className="p-2 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="px-3 py-1 text-sm">
                        Page {data.pagination.page} of {data.pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={!data.pagination.hasNext}
                        className="p-2 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
